import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Typography, Toolbar, AppBar, Tabs, Tab, styled, useTheme, useMediaQuery,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Pagination, CircularProgress, IconButton,
  Button, Modal, Chip, MenuItem, FormControl, InputLabel, Select,
  Dialog, DialogTitle, DialogContent, Divider, Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  CalendarMonth as CalendarIcon,
  Business as BusinessIcon,
  Reply as ReplyIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import LeftToolbar from "./LeftToolbar";
import Footer from "./Footer";
import NotificationSnackbar from "./NotificationBar";

const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box, {
  shouldForwardProp: (p) => p !== "collapsed",
})(({ theme, collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: { marginLeft: 0, padding: theme.spacing(2) },
}));

const PAGE_SIZE = 5;


const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const SUPPORT_API = `${API_BASE}/api/support`;

const ENDPOINTS = {
  pending: `${SUPPORT_API}/PendingITServicesRequest`,
  responded: `${SUPPORT_API}/RespondedITServicesRequest`,
};

const CREATE_REQUEST_ENDPOINT = `${SUPPORT_API}/CreateITServiceRequest`;
const EMPLOYEE_SEARCH_ENDPOINT = `${SUPPORT_API}/find-employee-from-entity-sector`;

// Service / Sub-service endpoints
const SERVICES_ENDPOINT = `${SUPPORT_API}/GetITServices`;
const SUBSERVICES_ENDPOINT = `${SUPPORT_API}/GetITSubServices`;

// Respond & close ticket: POST to /api/support/RespondedITServicesRequest
// (the same path the Responded list reads with GET)
const RESPOND_ENDPOINT = `${SUPPORT_API}/RespondedITServicesRequest`;
// TODO: confirm this route with your backend
const ASSIGN_ENDPOINT = `${SUPPORT_API}/AssignITServiceRequest`;

const IT_SERVICE_CATEGORY_ID = 1013;

// Document host (same one used by RespondedTickets)
const DOCS_BASE =
  process.env.REACT_APP_DOCS_BASE || "https://documents.ippis.rw";
const attachmentUrl = (refPath) => `${DOCS_BASE}/preview/${refPath}`;

// Same limits as the backend fileGuard
const ALLOWED_EXTENSIONS = ["pdf", "png", "jpg", "jpeg"];
const MAX_FILE_MB = 6;

// Returns an error message, or null when the file is fine
const validateFile = (file) => {
  if (!file) return null;
  const ext = file.name.split(".").pop().toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return "Only PDF, PNG, JPG or JPEG files are allowed.";
  }
  if (file.size > MAX_FILE_MB * 1024 * 1024) {
    return `File is too large (max ${MAX_FILE_MB} MB).`;
  }
  return null;
};

// Reads a response once and returns { data, error } (error is null when ok).
// If the server crashed and sent an HTML page, a short readable text is shown.
const parseResponse = async (res) => {
  const text = await res.text().catch(() => "");
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* not JSON */
  }
  let error = null;
  if (!res.ok) {
    const fromBody = typeof data === "string" ? data : data?.message;
    const plain = !data && text ? text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 120) : "";
    error = fromBody || `HTTP ${res.status}${plain ? `: ${plain}` : ""}`;
  }
  return { data, error };
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC",
      })
    : "-";

const getId = (r) => r.id ?? r.Id ?? r.TicketId ?? r.RequestId;
const getDescription = (r) =>
  r.ticketDescription ??
  r.TicketDescription ??
  r.Description ??
  r.ItServiceDescription ??
  r.RequestDescription ??
  "";
const getSender = (r) => r.Sender ?? r.SenderName ?? r.RequestedBy ?? "-";
const getInstitution = (r) => r.InstitutionName ?? r.EntityName ?? "-";
const getCreatedOn = (r) => r.CreatedOn ?? r.createdOn ?? r.RequestDate;
const getRespondedBy = (r) =>
  r.RespondedBy ?? r.respondedBy ?? r.AssignedTo ?? "-";
const getRespondedOn = (r) =>
  r.ClosedOn ?? r.closedOn ?? r.RespondedOn ?? r.respondedOn ?? r.ResponseDate;

// Service / sub-service helpers (keep ID/label key lookups in one place)
const getServiceValue = (s) => s.ServiceId ?? s.serviceId ?? s.id ?? s.Id;
const getServiceLabel = (s) =>
  s.name ??
  s.Name ??
  s.serviceName ??
  s.ServiceName ??
  `Service ${getServiceValue(s)}`;
const getSubServiceValue = (s) =>
  s.SubServiceId ?? s.subServiceId ?? s.id ?? s.Id;
const getSubServiceLabel = (s) =>
  s.name ??
  s.Name ??
  s.subServiceName ??
  s.SubServiceName ??
  `Sub-service ${getSubServiceValue(s)}`;

// New requests store "Service: ...\nSub-service: ...\n<text>" in the description
const parseDescription = (text) => {
  let service = "";
  let sub = "";
  const rest = [];
  String(text ?? "")
    .split("\n")
    .forEach((line) => {
      if (line.startsWith("Service: ")) service = line.slice(9);
      else if (line.startsWith("Sub-service: ")) sub = line.slice(13);
      else rest.push(line);
    });
  return { service, sub, body: rest.join("\n").trim() };
};

const InfoRow = ({ icon, label, children }) => (
  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
    {icon}
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{children || "-"}</Typography>
    </Box>
  </Box>
);

/* ---------- Ticket details dialog (same look as the User Information dialog) ---------- */
const TicketDetailsDialog = ({
  ticket,
  onClose,
  canRespond = false,
  canAssign = false,
  userId,
  onNotify,
  onDone,
}) => {
  const theme = useTheme();
  const brand = theme.palette.primary.light; // same blue as the table header

  const [mode, setMode] = useState(null); // null | "respond" | "assign"
  const [busy, setBusy] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [responseFile, setResponseFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [searchMsg, setSearchMsg] = useState(null);
  const [assignee, setAssignee] = useState(null);

  const canAct = canRespond || canAssign;
  const ticketId = ticket ? getId(ticket) : null;

  // Start clean every time a different ticket is opened
  useEffect(() => {
    setMode(null);
    setBusy(false);
    setResponseText("");
    setResponseFile(null);
    setSearchTerm("");
    setResults([]);
    setSearchMsg(null);
    setAssignee(null);
  }, [ticketId]);

  const { service, sub, body } = parseDescription(
    ticket ? getDescription(ticket) : ""
  );
  const attachment = ticket?.AttachmentRefPath ?? ticket?.attachmentRefPath;
  const sender = ticket ? String(getSender(ticket)) : "";

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const send = async (url, payload, isForm = false) => {
    const userToken = localStorage.getItem("userToken");
    if (!userToken) {
      onNotify?.("Unauthorized. Please login first.", "error");
      return;
    }
    try {
      setBusy(true);
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: `${userToken}`,
          ...(isForm ? {} : { "Content-Type": "application/json" }),
        },
        body: isForm ? payload : JSON.stringify(payload),
      });
      const { data, error } = await parseResponse(res);
      if (error) throw new Error(error);
      onNotify?.(data?.message || "Done successfully!", "success");
      onDone?.();
    } catch (err) {
      console.error("Ticket action failed:", err);
      onNotify?.(err?.message || "Action failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const hasUser = userId !== null && userId !== undefined && userId !== "";

  const handleRespond = () => {
    if (!hasUser) {
      onNotify?.("Could not identify your account. Please login again.", "error");
      return;
    }
    if (!responseText.trim()) {
      onNotify?.("Please write your response.", "warning");
      return;
    }
    const fd = new FormData();
    fd.append("ticketId", String(ticketId));
    fd.append("response", responseText.trim());
    fd.append("respondedBy", String(userId)); // logged-in user (user.id)
    if (responseFile) fd.append("file", responseFile); // fileGuard("file", ...)
    send(RESPOND_ENDPOINT, fd, true);
  };

  const handleAssign = () => {
    if (!hasUser) {
      onNotify?.("Could not identify your account. Please login again.", "error");
      return;
    }
    if (!assignee) {
      onNotify?.("Please select who to assign this ticket to.", "warning");
      return;
    }
    send(ASSIGN_ENDPOINT, {
      ticketId,
      assigneeId: assignee.id,
      assigneeIdNumber: assignee.idNumber,
      assignedBy: userId, // logged-in user (user.id)
    });
  };

  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) return;
    try {
      setSearching(true);
      setSearchMsg(null);
      const res = await fetch(EMPLOYEE_SEARCH_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ entitySectorId: null, searchKeyword: term }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setResults([]);
        setSearchMsg(
          (typeof json === "string" ? json : json?.message || json?.Message) ||
            `HTTP ${res.status}`
        );
        return;
      }
      const list = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
          ? json.data
          : json
            ? [json]
            : [];
      setResults(list);
      setSearchMsg(list.length ? null : "No record found");
    } catch (err) {
      console.error("Assignee search failed:", err);
      setResults([]);
      setSearchMsg("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const personName = (u) =>
    `${u.lastName || ""} ${u.firstName || ""}`.trim() || "-";

  return (
    <Dialog
      open={Boolean(ticket)}
      onClose={handleClose}
      maxWidth={canAct ? "md" : "sm"}
      fullWidth
      PaperProps={{
        sx: { borderRadius: { xs: 0, sm: 3 }, m: { xs: 0, sm: 2 } },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: brand,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Ticket {ticketId ?? ""}
        </Typography>
        <IconButton onClick={handleClose} disabled={busy} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4, pt: "32px !important" }}>
        {ticket && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: canAct ? "1fr 1fr" : "1fr" },
              gap: 4,
              alignItems: "start",
            }}
          >
            {/* Left column: ticket details */}
            <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
              <Avatar
                sx={{ width: 64, height: 64, bgcolor: brand, fontSize: "1.5rem" }}
              >
                {sender.trim()[0] ?? "U"}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {sender}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getInstitution(ticket)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <InfoRow icon={<CalendarIcon sx={{ color: brand }} />} label="Created on">
                {formatDate(getCreatedOn(ticket))}
              </InfoRow>
              {service && (
                <InfoRow icon={<BusinessIcon sx={{ color: brand }} />} label="Service">
                  {service}
                </InfoRow>
              )}
              {sub && (
                <InfoRow icon={<BusinessIcon sx={{ color: brand }} />} label="Sub-service">
                  {sub}
                </InfoRow>
              )}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 3 }}
            >
              Description
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                p: 2,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {body || "-"}
            </Box>

            {/* View attachment – same host used by RespondedTickets */}
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" color="text.secondary">
                View Attachment:
              </Typography>
              {attachment ? (
                <Button
                  component="a"
                  href={attachmentUrl(attachment)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  color="primary"
                  startIcon={<VisibilityIcon />}
                  sx={{ textTransform: "none", color: brand }}
                >
                  View attachment
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No attachment available
                </Typography>
              )}
            </Box>

            </Box>

            {/* Right column: Respond / Assign actions (pending tickets only) */}
            {canAct && (
              <Box
                sx={{
                  borderLeft: { md: "1px solid #e2e8f0" },
                  pl: { md: 4 },
                  borderTop: { xs: "1px solid #e2e8f0", md: "none" },
                  pt: { xs: 3, md: 0 },
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Actions
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {canRespond && (
                  <Button
                    variant={mode === "respond" ? "contained" : "outlined"}
                    disableElevation
                    startIcon={<ReplyIcon />}
                    onClick={() => setMode(mode === "respond" ? null : "respond")}
                    disabled={busy}
                    sx={{
                      flex: 1,
                      py: 1.25,
                      borderRadius: 2,
                      textTransform: "none",
                      borderColor: brand,
                      ...(mode === "respond"
                        ? { bgcolor: brand }
                        : { color: brand }),
                    }}
                  >
                    Respond
                  </Button>
                  )}
                  {canAssign && (
                  <Button
                    variant={mode === "assign" ? "contained" : "outlined"}
                    disableElevation
                    startIcon={<PersonAddIcon />}
                    onClick={() => setMode(mode === "assign" ? null : "assign")}
                    disabled={busy}
                    sx={{
                      flex: 1,
                      py: 1.25,
                      borderRadius: 2,
                      textTransform: "none",
                      borderColor: brand,
                      ...(mode === "assign"
                        ? { bgcolor: brand }
                        : { color: brand }),
                    }}
                  >
                    Assign
                  </Button>
                  )}
                </Box>

                {canRespond && mode === "respond" && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="Your response"
                      fullWidth
                      multiline
                      rows={4}
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Type your response to the requester..."
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1.5 }}>
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        startIcon={<AttachFileIcon />}
                        sx={{ borderRadius: 2, textTransform: "none" }}
                      >
                        Attach file (optional)
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            const err = validateFile(f);
                            if (err) {
                              onNotify?.(err, "warning");
                              return;
                            }
                            setResponseFile(f);
                          }}
                        />
                      </Button>
                      {responseFile && (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={responseFile.name}
                          >
                            {responseFile.name}
                          </Typography>
                          <IconButton size="small" onClick={() => setResponseFile(null)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      disableElevation
                      onClick={handleRespond}
                      disabled={busy || !responseText.trim()}
                      sx={{
                        mt: 2,
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: "none",
                        bgcolor: brand,
                        "&:hover": { bgcolor: theme.palette.primary.main },
                      }}
                    >
                      {busy ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Send response"
                      )}
                    </Button>
                  </Box>
                )}

                {canAssign && mode === "assign" && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        placeholder="ID number, phone, email or name..."
                        size="small"
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        disableElevation
                        onClick={handleSearch}
                        disabled={searching || !searchTerm.trim()}
                        startIcon={
                          searching ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SearchIcon />
                          )
                        }
                        sx={{
                          textTransform: "none",
                          borderRadius: 999,
                          px: 2.5,
                          bgcolor: brand,
                        }}
                      >
                        Search
                      </Button>
                    </Box>

                    {assignee && (
                      <Box sx={{ mt: 1.5 }}>
                        <Chip
                          color="primary"
                          variant="outlined"
                          label={`${personName(assignee)}${
                            assignee.idNumber ? ` - ${assignee.idNumber}` : ""
                          }`}
                          onDelete={() => setAssignee(null)}
                        />
                      </Box>
                    )}

                    {searchMsg && (
                      <Typography color="error" sx={{ fontSize: "0.85rem", mt: 1.5 }}>
                        {searchMsg}
                      </Typography>
                    )}

                    {results.length > 0 && (
                      <Box
                        sx={{
                          mt: 1.5,
                          maxHeight: 200,
                          overflowY: "auto",
                          border: "1px solid #e2e8f0",
                          borderRadius: 2,
                        }}
                      >
                        {results.map((u, idx) => (
                          <Box
                            key={u.id || `${u.idNumber}-${idx}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              px: 2,
                              py: 1,
                              borderBottom:
                                idx < results.length - 1 ? "1px solid #e2e8f0" : "none",
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {personName(u)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {u.idNumber || "-"} · {u.entityName || "-"}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setAssignee(u);
                                setResults([]);
                                setSearchMsg(null);
                                setSearchTerm("");
                              }}
                              sx={{ textTransform: "none", borderRadius: 2 }}
                            >
                              Select
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      fullWidth
                      disableElevation
                      onClick={handleAssign}
                      disabled={busy || !assignee}
                      sx={{
                        mt: 2,
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: "none",
                        bgcolor: brand,
                        "&:hover": { bgcolor: theme.palette.primary.main },
                      }}
                    >
                      {busy ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Assign ticket"
                      )}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Shared table ---------- */
const RequestsTable = ({
  status,
  onView,
  refreshKey = 0,
  onRequestClick,
  scopeUserId = null, // null = all tickets
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [serverPaged, setServerPaged] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const isResponded = status === "responded";

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!ENDPOINTS[status]) {
        setRows([]);
        setTotal(0);
        setError("This list is not connected to an API yet.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const qs = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
          search,
        });
        if (scopeUserId) qs.set("userId", String(scopeUserId));
        const res = await fetch(`${ENDPOINTS[status]}?${qs.toString()}`, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          throw new Error(
            "Server did not return JSON. Check the endpoint path and proxy."
          );
        }
        const data = await res.json();
        if (!res.ok) {
          throw new Error(
            (typeof data === "string" ? data : data?.message) ||
              `HTTP ${res.status}`
          );
        }
        if (!cancelled) {
          if (Array.isArray(data)) {
            const paged = data.length > 0 && data[0].TotalCount !== undefined;
            setServerPaged(paged);
            setRows(data);
            setTotal(paged ? Number(data[0].TotalCount) || 0 : data.length);
          } else {
            const list = Array.isArray(data?.tickets)
              ? data.tickets
              : Array.isArray(data?.data)
                ? data.data
                : [];
            setServerPaged(true);
            setRows(list);
            setTotal(Number(data?.total ?? list[0]?.TotalCount) || list.length);
          }
        }
      } catch (e) {
        console.error("IT services request fetch failed:", e);
        if (!cancelled) {
          setError(e.message || "Failed to load requests.");
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [status, page, search, refreshKey, scopeUserId]);

  const term = search.toLowerCase();
  const matched = serverPaged
    ? rows
    : rows.filter((r) =>
        [getId(r), getDescription(r), getSender(r), getInstitution(r)].some(
          (v) => String(v ?? "").toLowerCase().includes(term)
        )
      );
  const visibleRows = serverPaged
    ? rows
    : matched.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalCount = serverPaged ? total : matched.length;

  const columns = [
    "Id",
    "Description",
    "Sender",
    "Institution",
    "Date",
    ...(isResponded ? ["Responded By", "Responded On"] : []),
    "Actions",
  ];

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
      }}
    >
      {/* Search box + Request button + count */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <TextField
          label="Search requests..."
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{
            width: isMobile ? "100%" : 400,
            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {status === "pending" && onRequestClick && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onRequestClick}
            sx={{
              fontWeight: 600,
              borderRadius: "999px",
              textTransform: "none",
              bgcolor: theme.palette.primary.light,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Request IT Service
          </Button>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {totalCount} {totalCount === 1 ? "request" : "requests"} found
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderRadius: "16px", boxShadow: "0 8px 32px rgba(0,0,0,0.05)" }}
      >
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,
                "& th": { color: "white", fontWeight: 600, whiteSpace: "nowrap" },
              }}
            >
              {columns.map((c) => (
                <TableCell key={c}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading requests...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography color="error">{error}</Typography>
                </TableCell>
              </TableRow>
            ) : visibleRows.length ? (
              visibleRows.map((r, i) => {
                const desc = String(getDescription(r));
                return (
                  <TableRow key={getId(r) ?? i} hover>
                    <TableCell>{getId(r)}</TableCell>
                    <TableCell>
                      {desc.length > 30 ? `${desc.substring(0, 27)}...` : desc}
                    </TableCell>
                    <TableCell>{getSender(r)}</TableCell>
                    <TableCell>{getInstitution(r)}</TableCell>
                    <TableCell>{formatDate(getCreatedOn(r))}</TableCell>
                    {isResponded && <TableCell>{getRespondedBy(r)}</TableCell>}
                    {isResponded && (
                      <TableCell>{formatDate(getRespondedOn(r))}</TableCell>
                    )}
                    <TableCell>
                      <IconButton color="primary" onClick={() => onView?.(r)}>
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography>No requests found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
          page={page}
          onChange={(e, v) => setPage(v)}
          color="primary"
          size={isMobile ? "small" : "medium"}
        />
      </Box>
    </Box>
  );
};

/* ---------- The two views ---------- */
const PendingITServicesRequest = ({
  refreshKey,
  onRequestClick,
  onView,
  scopeUserId,
}) => (
  <RequestsTable
    status="pending"
    refreshKey={refreshKey}
    onRequestClick={onRequestClick}
    onView={onView}
    scopeUserId={scopeUserId}
  />
);
const RespondedITServicesRequest = ({ onView, scopeUserId }) => (
  <RequestsTable status="responded" onView={onView} scopeUserId={scopeUserId} />
);

/* ---------- Page ---------- */
const ITServicesRequest = () => {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("ITServicesRequest");
  const [searchParams, setSearchParams] = useSearchParams();

  // Logged-in user saved at login
  const [user] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : {};
    } catch (error) {
      console.error("Error parsing user data:", error);
      return {};
    }
  });
  const userId = user?.id ?? null;

  const userName = `${user?.lastName ?? ""} ${user?.firstName ?? ""}`.trim();

  // Roles. "ITSERVICES" also matches IT_SERVICES / IT SERVICES.
  const userRole = String(user?.roleId ?? "")
    .toUpperCase()
    .replace(/[\s_-]/g, "");
  const isAdminOrSupport = ["ADMIN", "SUPPORT"].includes(userRole);
  const isITServices = userRole === "ITSERVICES";

  const canRespond = isAdminOrSupport || isITServices; // respond / close tickets
  const canAssign = isAdminOrSupport; // assign tickets
  const seeAllTickets = isAdminOrSupport || isITServices; // pending + responded, everyone's
  // Other users only get their own tickets (the backend must filter by this id)
  const listUserId = seeAllTickets ? null : userId;

  // Modal state
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestDescription, setRequestDescription] = useState("");
  const [requestFile, setRequestFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Service / Sub-service state
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState(null);
  const [selectedService, setSelectedService] = useState("");

  const [subServices, setSubServices] = useState([]);
  const [subServicesLoading, setSubServicesLoading] = useState(false);
  const [subServicesError, setSubServicesError] = useState(null);
  const [selectedSubService, setSelectedSubService] = useState("");

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const tab = searchParams.get("tab") === "responded" ? "responded" : "pending";

  useEffect(() => setSelectedMenu("ITServicesRequest"), []);

  /* ----- Load Services when modal opens ----- */
  useEffect(() => {
    if (!requestOpen) return;
    let cancelled = false;

    const load = async () => {
      try {
        setServicesLoading(true);
        setServicesError(null);
        const res = await fetch(SERVICES_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(
            (typeof data === "string" ? data : data?.message) ||
              `HTTP ${res.status}`
          );
        }
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        if (!cancelled) setServices(list);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load services:", err);
          setServicesError(err.message || "Failed to load services.");
          setServices([]);
        }
      } finally {
        if (!cancelled) setServicesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [requestOpen]);

  /* ----- Load Sub-services when Service changes ----- */
  useEffect(() => {
    if (!requestOpen || selectedService === "") {
      setSubServices([]);
      setSelectedSubService("");
      setSubServicesError(null);
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        setSubServicesLoading(true);
        setSubServicesError(null);
        setSubServices([]);
        setSelectedSubService("");
        const res = await fetch(
          `${SUBSERVICES_ENDPOINT}?serviceId=${encodeURIComponent(
            selectedService
          )}`,
          {
            method: "GET",
            credentials: "include",
            headers: { Accept: "application/json" },
          }
        );
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(
            (typeof data === "string" ? data : data?.message) ||
              `HTTP ${res.status}`
          );
        }
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        if (!cancelled) setSubServices(list);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load sub-services:", err);
          setSubServicesError(err.message || "Failed to load sub-services.");
        }
      } finally {
        if (!cancelled) setSubServicesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [requestOpen, selectedService]);

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const resetRequestForm = () => {
    setRequestDescription("");
    setRequestFile(null);
    setSelectedService("");
    setSelectedSubService("");
    setSubServices([]);
  };

  const handleCloseRequest = () => {
    if (sending) return;
    setRequestOpen(false);
    resetRequestForm();
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    const userToken = localStorage.getItem("userToken");
    if (!userToken) {
      handleSnackbarOpen("Unauthorized. Please login first.", "error");
      return;
    }
    if (userId === null || userId === undefined || userId === "") {
      handleSnackbarOpen(
        "Could not identify your account. Please login again.",
        "error"
      );
      return;
    }
    if (selectedService === "") {
      handleSnackbarOpen("Please select a service.", "warning");
      return;
    }
    if (selectedSubService === "") {
      handleSnackbarOpen("Please select a sub-service.", "warning");
      return;
    }
    if (!requestDescription.trim()) {
      handleSnackbarOpen("Please describe your request.", "warning");
      return;
    }

    // The route expects service NAMES, so look them up from the loaded lists
    const serviceObj = services.find(
      (s) => String(getServiceValue(s)) === String(selectedService)
    );
    const serviceName = serviceObj ? getServiceLabel(serviceObj) : "";

    const subObj = subServices.find(
      (s) => String(getSubServiceValue(s)) === String(selectedSubService)
    );
    const subServiceName = subObj ? getSubServiceLabel(subObj) : "";

    const formData = new FormData();
    formData.append("categoryId", String(IT_SERVICE_CATEGORY_ID));
    formData.append("servicename", serviceName);
    formData.append("Subservicename", subServiceName);
    formData.append("Description", requestDescription.trim());
    formData.append("serviceId", String(selectedService));
    formData.append("subServiceId", String(selectedSubService));

    // The requester is the logged-in user (localStorage "userData")
    formData.append("userId", String(userId));
    formData.append("idNumber", user?.idNumber ?? "");
    formData.append("phoneNumber", user?.phoneNumber ?? "");
    formData.append("email", user?.email ?? "");
    // field name must be "file" because the route uses fileGuard("file", ...)
    if (requestFile) formData.append("file", requestFile);

    try {
      setSending(true);
      const res = await fetch(CREATE_REQUEST_ENDPOINT, {
        method: "POST",
        credentials: "include",
        body: formData, // no Content-Type: the browser sets the multipart boundary
        headers: { Authorization: `${userToken}` },
      });
      const { data, error } = await parseResponse(res);
      if (error) throw new Error(error);
      handleSnackbarOpen(
        data?.message || "Request sent successfully!",
        "success"
      );
      setRefreshKey((k) => k + 1);
      setSending(false);
      setRequestOpen(false);
      resetRequestForm();
    } catch (err) {
      console.error("Create IT service request failed:", err);
      handleSnackbarOpen(err?.message || "Failed to send request.", "error");
      setSending(false);
    }
  };

  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          zIndex: (t) => t.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"]),
          [theme.breakpoints.down("sm")]: { left: 0, width: "100%" },
        }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            IPPIS SUPPORT PORTAL
          </Typography>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
        onCollapse={setCollapsed}
      />

      <MainContent collapsed={collapsed}>
        <Toolbar />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            IT Services Request
          </Typography>
        </Box>

        <Tabs
          value={tab}
          onChange={(e, v) => setSearchParams({ tab: v })}
          sx={{ mb: 2 }}
        >
          <Tab
            value="pending"
            label="Pending IT Services Request"
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <Tab
            value="responded"
            label="Responded IT Services Request"
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
        </Tabs>

        {tab === "pending" ? (
          <PendingITServicesRequest
            refreshKey={refreshKey}
            onRequestClick={() => setRequestOpen(true)}
            onView={setSelectedTicket}
            scopeUserId={listUserId}
          />
        ) : (
          <RespondedITServicesRequest
            onView={setSelectedTicket}
            scopeUserId={listUserId}
          />
        )}
      </MainContent>

      <Footer collapsed={collapsed} />

      {/* Request IT Service modal */}
      <Modal
        open={requestOpen}
        onClose={handleCloseRequest}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmitRequest}
          sx={{
            width: { xs: "90%", sm: 760 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            outline: "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              p: 3,
              borderRadius: "12px 12px 0 0",
              position: "relative",
            }}
          >
            <IconButton
              onClick={handleCloseRequest}
              disabled={sending}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "white",
                bgcolor: "rgba(255, 255, 255, 0.1)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{ color: "white", fontWeight: 600, pr: 5 }}
            >
              Request IT Service
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{ mb: 2, color: "text.secondary", fontSize: "0.9rem" }}
            >
              Select the service you need, then the sub-service, and finally
              describe your request. The IPPIS support team will review and
              respond.
            </Typography>

            {userName && (
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                label={`Requesting as: ${userName}`}
                sx={{ mb: 2 }}
              />
            )}

            {/* Step 1: Service & Sub-service */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: "rgba(21, 101, 192, 0.03)",
              }}
            >
              <Typography
                sx={{ fontWeight: 600, fontSize: "0.9rem", mb: 1.5 }}
              >
                1. Select service and sub-service
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: 240, flex: 1 }}
                >
                  <InputLabel id="service-label">Service</InputLabel>
                  <Select
                    labelId="service-label"
                    label="Service"
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    disabled={servicesLoading}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                  >
                    {servicesLoading && (
                      <MenuItem disabled value="">
                        <CircularProgress size={16} sx={{ mr: 1 }} /> Loading
                        services...
                      </MenuItem>
                    )}
                    {!servicesLoading && servicesError && (
                      <MenuItem disabled value="">
                        <Typography color="error" variant="body2">
                          {servicesError}
                        </Typography>
                      </MenuItem>
                    )}
                    {!servicesLoading &&
                      !servicesError &&
                      services.length === 0 && (
                        <MenuItem disabled value="">
                          No services available
                        </MenuItem>
                      )}
                    {services.map((s) => {
                      const value = getServiceValue(s);
                      return (
                        <MenuItem key={value} value={value}>
                          {getServiceLabel(s)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  size="small"
                  sx={{ minWidth: 240, flex: 1 }}
                  disabled={selectedService === "" || subServicesLoading}
                >
                  <InputLabel id="subservice-label">Sub-service</InputLabel>
                  <Select
                    labelId="subservice-label"
                    label="Sub-service"
                    value={selectedSubService}
                    onChange={(e) => setSelectedSubService(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                  >
                    {subServicesLoading && (
                      <MenuItem disabled value="">
                        <CircularProgress size={16} sx={{ mr: 1 }} /> Loading
                        sub-services...
                      </MenuItem>
                    )}
                    {!subServicesLoading && subServicesError && (
                      <MenuItem disabled value="">
                        <Typography color="error" variant="body2">
                          {subServicesError}
                        </Typography>
                      </MenuItem>
                    )}
                    {!subServicesLoading &&
                      !subServicesError &&
                      subServices.length === 0 && (
                        <MenuItem disabled value="">
                          {selectedService !== ""
                            ? "No sub-services available"
                            : "Select a service first"}
                        </MenuItem>
                      )}
                    {subServices.map((s) => {
                      const value = getSubServiceValue(s);
                      return (
                        <MenuItem key={value} value={value}>
                          {getSubServiceLabel(s)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Step 2: Description */}
            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", mb: 1 }}>
              2. Describe your request
            </Typography>
            <TextField
              label="Request description"
              fullWidth
              multiline
              rows={5}
              required
              value={requestDescription}
              onChange={(e) => setRequestDescription(e.target.value)}
              placeholder="Type your request here..."
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper",
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                Attach file (optional)
                <input
                  type="file"
                  hidden
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    const err = validateFile(f);
                    if (err) {
                      handleSnackbarOpen(err, "warning");
                      return;
                    }
                    setRequestFile(f);
                  }}
                />
              </Button>
              {requestFile && (
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={requestFile.name}
                >
                  {requestFile.name}
                </Typography>
              )}
              {requestFile && (
                <IconButton size="small" onClick={() => setRequestFile(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleCloseRequest}
                disabled={sending}
                sx={{ borderRadius: 2, textTransform: "none", px: 3 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  selectedService === "" ||
                  selectedSubService === "" ||
                  !requestDescription.trim() ||
                  sending
                }
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                }}
              >
                {sending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Send Request"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <TicketDetailsDialog
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
        canRespond={tab === "pending" && canRespond}
        canAssign={tab === "pending" && canAssign}
        userId={userId}
        onNotify={handleSnackbarOpen}
        onDone={() => {
          setSelectedTicket(null);
          setRefreshKey((k) => k + 1);
        }}
      />

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </MainContainer>
  );
};

export default ITServicesRequest;